import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import { randUuid } from 'utils/functions';

const initialState = {
    toasters: [],
    snackbars: [],
    confirmationDialogs: [],
};

/**
 * Extract an unique id from toaster options.
 * @param {ToasterOptions} toasterOptions
 */
const toasterOptionsToId = (toasterOptions) => btoa(encodeURI(`${toasterOptions.message}-${toasterOptions.type}`));

const removeElementFromList = (elementsList, idToRemove) =>
    [...elementsList].filter((element) => {
        // when toaster is removed must cancel its hide timeout
        if (element.id === idToRemove) {
            if (element.autoHideTimer) {
                clearTimeout(element.autoHideTimer);
            }

            return false;
        }

        return true;
    });

class ToastersSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!ToastersSliceHandler.instance) {
            ToastersSliceHandler.instance = new ToastersSliceHandler();
        }

        return ToastersSliceHandler.instance;
    }

    constructor() {
        super('toastersSlice');

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            // The `reducers` field lets us define reducers and generate associated actions
            reducers: {
                /**
                 * Register a toaster.
                 * @param {object} state
                 * @param {{payload: ToasterOptions}} action
                 */
                addToasterAction(state, action) {
                    const id = toasterOptionsToId(action.payload);
                    const newToaster = { id, ...action.payload };

                    newToaster.onClose = () => {
                        ToastersSliceHandler.getInstance().dispatch('removeToasterAction', action.payload);
                    };

                    const duration = action.payload.duration ?? 5;

                    newToaster.hideTimer = setTimeout(() => {
                        newToaster.onClose();
                    }, duration * 1000);

                    const currentToasters = removeElementFromList(state.toasters, id);

                    currentToasters.unshift(newToaster);

                    state.toasters = [...currentToasters];
                },
                /**
                 * Remove a registered toaster.
                 * @param {object} state
                 * @param {{payload: ToasterOptions}} action
                 */
                removeToasterAction(state, action) {
                    const id = toasterOptionsToId(action.payload);

                    const currentToasters = removeElementFromList(state.toasters, id);

                    state.toasters = [...currentToasters];
                },
                /**
                 * Register a snackbar element.
                 * @param {object} state
                 * @param {{payload: SnackbarOptions}} action
                 */
                addSnackbarAction(state, action) {
                    const id = toasterOptionsToId(action.payload);
                    const newSnackbar = { id, ...action.payload };

                    newSnackbar.onClose = () => {
                        ToastersSliceHandler.getInstance().dispatch('removeSnackbarAction', action.payload);

                        if (typeof action.payload.onClose === 'function') {
                            action.payload.onClose();
                        }
                    };

                    const duration = action.payload.duration ?? 5;

                    newSnackbar.hideTimer = setTimeout(() => {
                        newSnackbar.onClose();
                    }, duration * 1000);

                    const currentSnackbars = removeElementFromList(state.snackbars, id);

                    currentSnackbars.unshift(newSnackbar);

                    state.snackbars = [...currentSnackbars];
                },
                /**
                 * Remove a registered snackbar.
                 * @param {object} state
                 * @param {{payload: SnackbarOptions}} action
                 */
                removeSnackbarAction(state, action) {
                    const id = toasterOptionsToId(action.payload);

                    const currentSnackbars = removeElementFromList(state.snackbars, id);

                    state.snackbars = [...currentSnackbars];
                },
                /**
                 * Register a standalone confirmation dialog element.
                 * @param {object} state
                 * @param {{payload: ConfirmationDialogOptions}} action
                 */
                addConfirmationDialogAction(state, action) {
                    const id = randUuid();

                    const newConfirmationDialog = { id, ...action.payload };

                    newConfirmationDialog.yesCallback = () => {
                        ToastersSliceHandler.getInstance().dispatch('removeConfirmationAction', id);

                        action.payload.yesCallback();
                    };

                    newConfirmationDialog.noCallback = () => {
                        ToastersSliceHandler.getInstance().dispatch('removeConfirmationAction', id);

                        action.payload.noCallback();
                    };

                    const currentConfirmationDialogs = removeElementFromList(state.confirmationDialogs, id);

                    currentConfirmationDialogs.unshift(newConfirmationDialog);

                    state.confirmationDialogs = [...currentConfirmationDialogs];
                },
                /**
                 * Remove a registered confirmation dialog.
                 * @param {object} state
                 * @param {{payload: SnackbarOptions}} action
                 */
                removeConfirmationAction(state, action) {
                    const currentConfirmationDialogs = removeElementFromList(state.confirmationDialogs, action.payload);

                    state.confirmationDialogs = [...currentConfirmationDialogs];
                },
            },
        });
    }

    /**
     * @param {ToasterOptions} toasterOptions
     */
    addToasterToStore = (toasterOptions) => {
        this.dispatch('addToasterAction', toasterOptions);
    };

    /**
     * @param {ToasterOptions} toasterOptions
     */
    removeToasterFromStore = (toasterOptions) => {
        this.dispatch('removeToasterAction', toasterOptions);
    };

    /**
     * @param {SnackbarOptions} snackbarOptions
     */
    addSnackbarToStore = (snackbarOptions) => {
        this.dispatch('addSnackbarAction', snackbarOptions);
    };

    /**
     * @param {SnackbarOptions} snackbarOptions
     */
    removeSnackbarFromStore = (snackbarOptions) => {
        this.dispatch('removeSnackbarAction', snackbarOptions);
    };

    /**
     * @param {ConfirmationDialogOptions} confirmationDialogOptions
     */
    addConfirmationDialogToStore = (confirmationDialogOptions) => {
        this.dispatch('addConfirmationDialogAction', confirmationDialogOptions);
    };
}

// the handler
export const toastersSliceHandler = ToastersSliceHandler.getInstance();
export const selectToasters = (state) => state.toastersSlice.toasters;
export const selectSnackbars = (state) => state.toastersSlice.snackbars;
export const selectConfirmationDialogs = (state) => state.toastersSlice.confirmationDialogs;
export const {
    addToasterToStore,
    removeToasterFromStore,
    addSnackbarToStore,
    removeSnackbarFromStore,
    addConfirmationDialogToStore,
} = toastersSliceHandler;
